import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  Avatar, 
  Divider, 
  Badge, 
  TextField as MuiTextField,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import ForumIcon from '@mui/icons-material/Forum';
import InfoIcon from '@mui/icons-material/Info';
import PaymentIcon from '@mui/icons-material/Payment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import { useSnackbar } from 'notistack';

import COLORS from '../../theme/Colors';
import FONTS_FAMILY from '../../theme/FontsFamily';
import { StyledButton, SmallHeightDivider } from '../../theme/Styles';
import CenterLoading from '../../components/CenterLoading/CenterLoading';

// APIs
import { 
  getRequestedServicesCitizenIDWithOutCompany, 
  getRequestedServicesCitizenIDWithAllCompany 
} from '../../api/RequestedServiceList';
import { getRequestDetail } from '../../api/ServiceRequestedDetails';
import { sendMessage } from '../../api/ActionRequired';
import { getAllCompanies } from '../../api/myCompany';

import { replaceGuionToSlashFromString, sanitizeHTML } from '../../utilities/functions/StringUtil';
import LocalStorageService from '../../services/LocalStorageService';

const safeDecode = (s) => {
  if (typeof s !== "string") return "";
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

const formatMessageDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const formattedDate = replaceGuionToSlashFromString(dateStr);
    return format(new Date(formattedDate), "dd 'de' MMMM yyyy hh:mm a", {
      locale: es,
    });
  } catch (err) {
    return dateStr;
  }
};

const formatListDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const formattedDate = replaceGuionToSlashFromString(dateStr);
    return format(new Date(formattedDate), "dd MMM yyyy", {
      locale: es,
    });
  } catch (err) {
    return dateStr;
  }
};

async function fetchAllPersonalRequestsForMessages(citizenID) {
  try {
    const firstPage = await getRequestedServicesCitizenIDWithOutCompany(citizenID, 1, 100, '', 100);
    if (!firstPage) return [];
    let allData = Array.isArray(firstPage) ? [...firstPage] : (Array.isArray(firstPage?.data) ? [...firstPage.data] : []);
    const totalPages = Math.min(firstPage?.last_page || 1, 15);
    if (totalPages > 1) {
      const promises = [];
      for (let p = 2; p <= totalPages; p++) {
        promises.push(getRequestedServicesCitizenIDWithOutCompany(citizenID, p, 100, '', 100).catch(() => null));
      }
      const results = await Promise.all(promises);
      results.forEach(res => {
        if (res && Array.isArray(res.data)) {
          allData = allData.concat(res.data);
        } else if (res && Array.isArray(res)) {
          allData = allData.concat(res);
        }
      });
    }
    return Array.isArray(allData) ? allData : [];
  } catch (err) {
    console.error("Error in fetchAllPersonalRequestsForMessages:", err);
    return [];
  }
}

async function fetchAllCompanyRequestsForMessages(citizenID) {
  try {
    const firstPage = await getRequestedServicesCitizenIDWithAllCompany(citizenID, 1, 100, '', 0, 0);
    if (!firstPage) return [];
    let allData = Array.isArray(firstPage) ? [...firstPage] : (Array.isArray(firstPage?.data) ? [...firstPage.data] : []);
    const totalPages = Math.min(firstPage?.last_page || 1, 15);
    if (totalPages > 1) {
      const promises = [];
      for (let p = 2; p <= totalPages; p++) {
        promises.push(getRequestedServicesCitizenIDWithAllCompany(citizenID, p, 100, '', 0, 0).catch(() => null));
      }
      const results = await Promise.all(promises);
      results.forEach(res => {
        if (res && Array.isArray(res.data)) {
          allData = allData.concat(res.data);
        } else if (res && Array.isArray(res)) {
          allData = allData.concat(res);
        }
      });
    }
    return Array.isArray(allData) ? allData : [];
  } catch (err) {
    console.error("Error in fetchAllCompanyRequestsForMessages:", err);
    return [];
  }
}

const hasMessagesOrObservations = (req) => {
  if (!req) return false;
  if (req.comments && Array.isArray(req.comments) && req.comments.length > 0) return true;
  if (Number(req.request_actions_id) === 3 || Number(req.action_id) === 3) return true;
  if (req.required_actions && Array.isArray(req.required_actions) && req.required_actions.length > 0) return true;
  if (req.requiredActions && Array.isArray(req.requiredActions) && req.requiredActions.length > 0) return true;
  if (req.request_actions !== null && req.request_actions !== undefined) return true;
  return false;
};

export default function MessagesPage() {
  const history = useHistory();
  const { citizenID, rolBase } = useSelector((state) => state.authReducer);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

  useEffect(() => {
    const handleLangChange = (e) => {
      if (e?.detail) {
        setCurrentLang(e.detail);
      } else {
        setCurrentLang(LocalStorageService.getItem('language') || 'es');
      }
    };
    window.addEventListener('languageChange', handleLangChange);
    return () => window.removeEventListener('languageChange', handleLangChange);
  }, []);

  const isEn = currentLang === 'en';

  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [newMessageText, setNewMessageText] = useState('');
  const messagesEndRef = useRef(null);

  // 1. Check if citizen has companies
  const { data: companiesData, isLoading: companiesLoading } = useQuery(
    ["allCompaniesData"],
    () => getAllCompanies(),
    { enabled: !!citizenID }
  );
  const hasCompanies = rolBase === 2 || (companiesData?.payload && companiesData.payload.length > 0);

  // 2. Fetch Personal Requests across all pages
  const { data: personalRequests, isLoading: personalLoading } = useQuery(
    ['personalRequestsForMessages', citizenID],
    () => fetchAllPersonalRequestsForMessages(citizenID),
    { enabled: !!citizenID }
  );

  // 3. Fetch Company Requests across all pages (if applicable)
  const { data: companyRequests, isLoading: companyLoading } = useQuery(
    ['companyRequestsForMessages', citizenID],
    () => fetchAllCompanyRequestsForMessages(citizenID),
    { enabled: !!citizenID && hasCompanies }
  );

  // Combine, deduplicate and filter requests that have messages/observations
  const personalList = Array.isArray(personalRequests)
    ? personalRequests
    : (Array.isArray(personalRequests?.data) ? personalRequests.data : []);

  const companyList = Array.isArray(companyRequests)
    ? companyRequests
    : (Array.isArray(companyRequests?.data) ? companyRequests.data : []);

  const allRawRequests = [...personalList, ...companyList];

  const uniqueRequestsMap = new Map();
  allRawRequests.forEach(req => {
    if (req && req.id && !uniqueRequestsMap.has(req.id)) {
      uniqueRequestsMap.set(req.id, req);
    }
  });

  const allRequests = Array.from(uniqueRequestsMap.values()).filter(hasMessagesOrObservations);

  // Sort requests by updated_at or created_at descending (newest first)
  allRequests.sort((a, b) => {
    const dateA = new Date(replaceGuionToSlashFromString(a.updated_at || a.created_at));
    const dateB = new Date(replaceGuionToSlashFromString(b.updated_at || b.created_at));
    return dateB - dateA;
  });

  // Auto-select the first request if none is selected
  useEffect(() => {
    if (allRequests.length > 0 && !selectedRequestId) {
      setSelectedRequestId(allRequests[0].id);
    }
  }, [allRequests, selectedRequestId]);

  const activeRequest = allRequests.find(r => r.id === selectedRequestId) || allRequests[0];

  // 4. Fetch details/comments for the active request
  const { data: requestDetail, isLoading: detailLoading } = useQuery(
    ['requestDetailForMessages', selectedRequestId],
    () => getRequestDetail(selectedRequestId, citizenID),
    { 
      enabled: !!selectedRequestId && !!citizenID,
      refetchInterval: 10000,
    }
  );

  const combinedMessages = useMemo(() => {
    const rawComments = requestDetail?.request?.comments || activeRequest?.comments || [];
    const reqActions = requestDetail?.request?.required_actions || 
                       requestDetail?.request?.requiredActions || 
                       activeRequest?.required_actions || 
                       activeRequest?.requiredActions || [];

    const list = [...rawComments.map(c => ({
      id: `c_${c.id || c.created_at}`,
      text: c.text,
      user_id: c.user_id,
      created_at: c.created_at,
      type: 'comment'
    }))];

    if (Array.isArray(reqActions)) {
      reqActions.forEach(act => {
        if (act && act.message) {
          list.push({
            id: `act_${act.id || act.created_at}`,
            text: act.message,
            user_id: 2,
            created_at: act.created_at,
            type: 'action',
            actionName: act.action?.name || 'Observación del Analista'
          });
        }
      });
    }

    const uniqueMap = new Map();
    list.forEach(item => {
      const key = `${item.text}_${item.created_at}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, item);
      }
    });

    const result = Array.from(uniqueMap.values());
    result.sort((a, b) => new Date(replaceGuionToSlashFromString(a.created_at || 0)) - new Date(replaceGuionToSlashFromString(b.created_at || 0)));
    return result;
  }, [requestDetail, activeRequest]);

  const comments = combinedMessages;
  const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;
  const lastCommentIsFromUser = lastComment ? (lastComment.user_id === null || lastComment.user_id === undefined) : false;

  const isMessageAction = Number(requestDetail?.request?.request_actions_id) === 3 ||
                          Number(activeRequest?.request_actions_id) === 3 ||
                          (requestDetail?.request?.required_actions && requestDetail.request.required_actions.some(a => Number(a.action_id) === 3)) ||
                          (requestDetail?.request?.requiredActions && requestDetail.request.requiredActions.some(a => Number(a.action_id) === 3));

  const isActionRequiredCompleted = requestDetail?.request?.required_actions?.some(a => a.status === true || a.status === 1 || a.completed === true);

  const showInputArea = isMessageAction && !isActionRequiredCompleted && !lastCommentIsFromUser && (requestDetail?.request?.status?.base_status_id === 1 || activeRequest?.status?.base_status_id === 1);

  // Scroll to bottom helper
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (comments.length > 0) {
      scrollToBottom();
    }
  }, [comments]);

  // Mutation to send a new message
  const messageMutation = useMutation(sendMessage);

  const handleSendMessage = () => {
    if (!newMessageText.trim() || !selectedRequestId) return;

    const payload = {
      request_id: selectedRequestId,
      text: newMessageText
    };

    messageMutation.mutate(payload, {
      onSuccess: () => {
        enqueueSnackbar("Mensaje enviado satisfactoriamente", { variant: "success" });
        setNewMessageText('');
        queryClient.invalidateQueries(['requestDetailForMessages', selectedRequestId]);
      },
      onError: () => {
        enqueueSnackbar("Ha ocurrido un error al enviar el mensaje", { variant: "error" });
      }
    });
  };

  const getRequestLastMessage = (req) => {
    if (req.id === selectedRequestId && comments.length > 0) {
      return safeDecode(comments[comments.length - 1].text);
    }
    if (req.comments && req.comments.length > 0) {
      return safeDecode(req.comments[req.comments.length - 1].text);
    }
    return 'Haga clic para ver la conversación';
  };

  const getRequestLastMessageDate = (req) => {
    if (req.id === selectedRequestId && comments.length > 0) {
      return formatListDate(comments[comments.length - 1].created_at);
    }
    if (req.comments && req.comments.length > 0) {
      return formatListDate(req.comments[req.comments.length - 1].created_at);
    }
    return formatListDate(req.created_at);
  };

  if (personalLoading || (hasCompanies && companyLoading) || companiesLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CenterLoading />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Main Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#192873', mb: 1, fontFamily: 'Roboto, sans-serif', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ForumIcon sx={{ fontSize: '36px', color: '#192873' }} />
          {isEn ? "Messages & Observations" : "Mensajes y Observaciones"}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Roboto, sans-serif' }}>
          {isEn 
            ? "Review observations and maintain direct communication with Ministry of Tourism analysts regarding your procedure applications."
            : "Consulte las observaciones y mantenga comunicación directa con los analistas del Ministerio de Turismo acerca de sus solicitudes de trámites."}
        </Typography>
      </Box>

      {allRequests.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: '12px', fontFamily: 'Roboto, sans-serif' }}>
          {isEn 
            ? "You have no procedure requests registered at this time. Messages and observations will appear when you start a procedure."
            : "No tienes solicitudes de trámites registradas en este momento. Los mensajes y observaciones aparecerán cuando inicies un trámite."}
        </Alert>
      ) : (
        <Grid container spacing={3} sx={{ flex: 1, minHeight: '500px' }}>
          {/* Left Side: Conversation List */}
          <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden', flex: 1, bgcolor: '#FFFFFF', display: 'flex', flexDirection: 'column', 'html.dark &': { bgcolor: '#0a0a0a', borderColor: '#222222' } }}>
              <Box sx={{ p: 2, bgcolor: 'rgba(3, 13, 57, 0.02)', borderBottom: '1px solid #E2E8F0', 'html.dark &': { bgcolor: '#111111', borderColor: '#222222' } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#192873', fontFamily: 'Roboto, sans-serif', 'html.dark &': { color: '#ffffff' } }}>
                  {isEn ? "Procedure Inbox" : "Bandeja de Trámites"}
                </Typography>
              </Box>
              <List sx={{ p: 0, overflowY: 'auto', flex: 1, maxHeight: '600px' }}>
                {allRequests.map((req) => {
                  const isSelected = req.id === selectedRequestId;
                  return (
                    <React.Fragment key={req.id}>
                      <ListItem 
                        button 
                        onClick={() => {
                          setSelectedRequestId(req.id);
                        }}
                        selected={isSelected}
                        sx={{
                          py: 2.5,
                          px: 3,
                          borderLeft: isSelected ? `4px solid ${COLORS.primary}` : '4px solid transparent',
                          bgcolor: isSelected ? 'rgba(3, 13, 57, 0.03)' : 'inherit',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: 'rgba(3, 13, 57, 0.01)'
                          }
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: '48px' }}>
                          <Avatar sx={{ bgcolor: COLORS.secondary, color: '#FFFFFF', width: 40, height: 40 }}>
                            <MarkEmailUnreadIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#192873', fontFamily: 'Roboto, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', 'html.dark &': { color: '#ffffff' } }}>
                                {req.service?.name || 'Servicio sin nombre'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1, minWidth: '70px', textAlign: 'right', fontFamily: 'Roboto, sans-serif' }}>
                                {getRequestLastMessageDate(req)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12.5px', fontFamily: 'Roboto, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', mb: 1 }}>
                                {getRequestLastMessage(req)}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box sx={{
                                  display: 'inline-flex', px: 1, py: 0.2, borderRadius: '4px', fontSize: '10px', fontWeight: 'bold',
                                  bgcolor: 'rgba(3, 13, 57, 0.05)', color: '#192873',
                                  'html.dark &': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: '#ffffff' }
                                }}>
                                  {req.code}
                                </Box>
                                <Box sx={{
                                  display: 'inline-flex', px: 1, py: 0.2, borderRadius: '4px', fontSize: '10px', fontWeight: 'bold',
                                  bgcolor: req.status?.id === 8 ? '#fef2f2' : req.status?.id === 10 ? '#fcf8e3' : '#f0fdf4',
                                  color: req.status?.id === 8 ? '#ef4444' : req.status?.id === 10 ? '#c19c00' : '#15803d',
                                }}>
                                  {req.status?.name || 'Sin Estado'}
                                </Box>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>
            </Paper>
          </Grid>

          {/* Right Side: Chat Window */}
          <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#FFFFFF', minHeight: '500px', 'html.dark &': { bgcolor: '#0a0a0a', borderColor: '#222222' } }}>
              {activeRequest ? (
                <>
                  {/* Active chat header */}
                  <Box sx={{ p: 2.5, borderBottom: '1px solid #E2E8F0', bgcolor: 'rgba(3, 13, 57, 0.02)', 'html.dark &': { bgcolor: '#111111', borderColor: '#222222' } }}>
                    <Box
                      onClick={() => history.push(`/app/serviceRequestedDetails/${activeRequest.id}`)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        '&:hover .title-text': { color: '#EA361A', textDecoration: 'underline' },
                        '&:hover .title-icon': { color: '#EA361A' }
                      }}
                    >
                      <Typography
                        className="title-text"
                        variant="subtitle1"
                        sx={{ fontWeight: 'bold', color: '#192873', fontFamily: 'Roboto, sans-serif', transition: 'color 0.2s', 'html.dark &': { color: '#ffffff' } }}
                      >
                        {activeRequest.service?.name || 'Servicio sin nombre'}
                      </Typography>
                      <OpenInNewIcon className="title-icon" sx={{ fontSize: 18, color: '#192873', transition: 'color 0.2s', 'html.dark &': { color: '#ffffff' } }} />
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'Roboto, sans-serif' }}>
                        Solicitud: <strong
                          onClick={() => history.push(`/app/serviceRequestedDetails/${activeRequest.id}`)}
                          style={{ cursor: 'pointer', color: '#192873', textDecoration: 'underline' }}
                        >{activeRequest.code}</strong> • Estado: <strong style={{ color: activeRequest.status?.id === 8 ? '#ef4444' : '#15803d' }}>{activeRequest.status?.name || 'Sin Estado'}</strong>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Chat history list */}
                  <Box sx={{ flex: 1, p: 3, overflowY: 'auto', bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column', gap: 2, height: '400px', 'html.dark &': { bgcolor: '#000000' } }}>
                    {detailLoading && comments.length === 0 ? (
                      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : comments.length > 0 ? (
                      <>
                        {comments.map((msg, index) => {
                          const isCitizen = msg.user_id != 2;
                          return (
                            <Box 
                              key={msg.id || index}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignSelf: isCitizen ? 'flex-end' : 'flex-start',
                                maxWidth: '80%'
                              }}
                            >
                              <Box 
                                sx={{
                                  p: 2,
                                  borderRadius: '16px',
                                  borderTopRightRadius: isCitizen ? '4px' : '16px',
                                  borderTopLeftRadius: !isCitizen ? '4px' : '16px',
                                  bgcolor: isCitizen ? COLORS.primary : '#FFFFFF',
                                  color: isCitizen ? '#FFFFFF' : '#192873',
                                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.02)',
                                  border: !isCitizen ? '1px solid #E2E8F0' : 'none',
                                  'html.dark &': !isCitizen ? { bgcolor: '#111111', color: '#ffffff', borderColor: '#222222' } : {}
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  sx={{ fontFamily: 'Roboto, sans-serif', lineHeight: 1.5 }}
                                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(safeDecode(msg.text)) }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary', alignSelf: isCitizen ? 'flex-end' : 'flex-start', fontSize: '10px', fontFamily: 'Roboto, sans-serif' }}>
                                {isCitizen ? 'Tú' : 'Analista MITUR'} • {formatMessageDate(msg.created_at)}
                              </Typography>
                            </Box>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </>
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#94a3b8',
                      }}>
                        <span style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }}>💬</span>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'Roboto, sans-serif', color: '#64748B' }}>
                          No hay mensajes
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'Roboto, sans-serif', color: '#94A3B8' }}>
                          Los mensajes de esta solicitud aparecerán aquí.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Chat Input Field */}
                  {showInputArea ? (
                    <Box sx={{ p: 2, borderTop: '1px solid #E2E8F0', display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <MuiTextField
                        fullWidth
                        variant="outlined"
                        placeholder="Escriba un mensaje aquí..."
                        size="small"
                        value={newMessageText}
                        onChange={(e) => setNewMessageText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '24px',
                            fontFamily: 'Roboto, sans-serif'
                          },
                          'html.dark & .MuiOutlinedInput-root': {
                            bgcolor: '#111111',
                            color: '#ffffff',
                          },
                          'html.dark & .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#222222',
                          },
                        }}
                      />
                      <IconButton 
                        color="primary" 
                        onClick={handleSendMessage}
                        disabled={!newMessageText.trim() || messageMutation.isLoading}
                        sx={{
                          bgcolor: COLORS.primary,
                          color: '#FFFFFF',
                          p: 1.2,
                          '&:hover': {
                            bgcolor: COLORS.secondary
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                            color: 'rgba(0, 0, 0, 0.25)'
                          }
                        }}
                      >
                        {messageMutation.isLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SendIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2.5, borderTop: '1px solid #E2E8F0', bgcolor: 'rgba(3, 13, 57, 0.01)', textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Roboto, sans-serif', fontWeight: 500 }}>
                        🔒 Esta solicitud no requiere observaciones ni respuestas de tu parte en este momento.
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '100%', color: '#64748B' }}>
                  Seleccione un trámite de la bandeja para ver la conversación.
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
